import { useCallback, useEffect, useRef, useState } from "react";

import { Box, Flex, Image } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, Text } from "components";
import { ServiceConstants, IDNOW_SUPPORTED_NATIONALITIES } from "constant";
import { BankSelectionService, XmlService } from "services";
import { Bank, BankSpecificResponse, BankSpecificStatus, BankCreditNoteResponse, NoteType, DocLanguage } from "types";
import { DistanceUtils } from "utils";

import bankNowLogo from "assets/banks/bank-now.png";
import cembraLogo from "assets/banks/cembra.png";
import cornerLogo from "assets/banks/corner.png";

import { BankTab } from "./components/bank-tab";
import { StatusBadge } from "./components/status-badge";

const OFFICE_ADDRESS = "Ochsengässli 9, 5000 Aarau";

interface ClientAddress {
  street?: string;
  houseNumber?: string;
  postal?: string;
  city?: string;
}

const {
  REQUEST_ERROR,
  REQUEST_BANK_SPECIFIC_GET_ID,
  REQUEST_BANK_SPECIFIC_AVAILABLE_STATUSES_ID,
  REQUEST_BANK_SPECIFIC_SAVE_ID,
  REQUEST_BANK_SPECIFIC_STATUS_CHANGE_ID,
  REQUEST_BANK_NOTES_LIST_ID,
  REQUEST_BANK_NOTE_CREATE_ID,
  REQUEST_PENDING,
} = ServiceConstants;

type SelectableBank = "BANK_NOW" | "CEMBRA_MONEY_AG" | "CORNER";

const BANK_TABS: { bank: SelectableBank; labelKey: string; logo: string }[] = [
  { bank: "BANK_NOW", labelKey: "bankSelection.bank.BANK_NOW", logo: bankNowLogo },
  { bank: "CEMBRA_MONEY_AG", labelKey: "bankSelection.bank.CEMBRA_MONEY_AG", logo: cembraLogo },
  { bank: "CORNER", labelKey: "bankSelection.bank.CORNER", logo: cornerLogo },
];

const EMPTY_DISABLED_BANKS: Bank[] = [];

type BankDataCache = {
  bankSpecific?: BankSpecificResponse;
  bankNotes: BankCreditNoteResponse[];
  processingNotes: BankCreditNoteResponse[];
  fetched: boolean;
};

const createInitialBankDataCache = (): Record<SelectableBank, BankDataCache> => ({
  BANK_NOW: { bankNotes: [], processingNotes: [], fetched: false },
  CEMBRA_MONEY_AG: { bankNotes: [], processingNotes: [], fetched: false },
  CORNER: { bankNotes: [], processingNotes: [], fetched: false },
});

const createInitialBankStatuses = (): Record<SelectableBank, BankSpecificStatus | undefined> => ({
  BANK_NOW: undefined,
  CEMBRA_MONEY_AG: undefined,
  CORNER: undefined,
});

const createInitialAvailableStatuses = (): Record<SelectableBank, BankSpecificStatus[] | null | undefined> => ({
  BANK_NOW: undefined,
  CEMBRA_MONEY_AG: undefined,
  CORNER: undefined,
});

interface BankSelectionProps {
  clientId: number;
  creditId: number;
  bankCreditRequestId: number;
  availableBanks: Bank[];
  disabledBanks?: Bank[];
  isExpanded: boolean;
  clientAddress?: ClientAddress;
  clientNationality?: string;
  clientVisaType?: "B" | "C";
  onBankStatusUpdated?: () => void;
}

export const BankSelection = ({
  clientId,
  creditId,
  bankCreditRequestId,
  availableBanks,
  disabledBanks = EMPTY_DISABLED_BANKS,
  isExpanded,
  clientAddress,
  clientNationality,
  clientVisaType,
  onBankStatusUpdated,
}: BankSelectionProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<SelectableBank>(BANK_TABS[0].bank);
  const [distanceFromOffice, setDistanceFromOffice] = useState<number | undefined>(undefined);
  const [distanceCalculating, setDistanceCalculating] = useState(false);

  const isOnlineAuthEligible =
    activeTab === "BANK_NOW" &&
    !!clientNationality &&
    IDNOW_SUPPORTED_NATIONALITIES.includes(clientNationality);
  const canGenerateXml = activeTab === "BANK_NOW" || activeTab === "CEMBRA_MONEY_AG";
  const [isXmlExporting, setIsXmlExporting] = useState(false);

  const handleXmlExport = async () => {
    if (!canGenerateXml || !clientId || isXmlExporting) return;
    setIsXmlExporting(true);
    try {
      await XmlService.downloadBankXml(activeTab, clientId);
    } catch (error) {
      console.error("XML export failed:", error);
      notifications.show({
        title: t("bankSelection.details.generateXml"),
        message: t("bankSelection.details.generateXmlError"),
        color: "red",
      });
    } finally {
      setIsXmlExporting(false);
    }
  };
  const [bankStatuses, setBankStatuses] = useState<Record<SelectableBank, BankSpecificStatus | undefined>>(
    createInitialBankStatuses
  );
  const [availableStatuses, setAvailableStatuses] = useState<Record<SelectableBank, BankSpecificStatus[] | null | undefined>>(
    createInitialAvailableStatuses
  );
  const [bankDataCache, setBankDataCache] = useState<Record<SelectableBank, BankDataCache>>(
    createInitialBankDataCache
  );

  const fetchingRef = useRef<Set<SelectableBank>>(new Set());
  const waitingForPendingRef = useRef<Set<SelectableBank>>(new Set());
  const fetchingAvailableStatusesRef = useRef<Set<SelectableBank>>(new Set());
  const attemptedAvailableStatusesRef = useRef<Set<SelectableBank>>(new Set());
  const availableStatusesRef = useRef(availableStatuses);
  const bankDataCacheRef = useRef(bankDataCache);
  const attemptedDistanceAddressRef = useRef<string | null>(null);
  bankDataCacheRef.current = bankDataCache;
  availableStatusesRef.current = availableStatuses;

  const bankSpecificResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_SPECIFIC_GET_ID + activeTab]
  );

  const serviceObjects = useSelector((state: any) => state.service.object);

  const bankNotesResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_NOTES_LIST_ID + "BANK"]
  );

  const processingNotesResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_NOTES_LIST_ID + "PROCESSING"]
  );

  const noteCreateResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_NOTE_CREATE_ID]
  );

  const bankSpecificSaveResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_SPECIFIC_SAVE_ID]
  );

  const bankSpecificStatusChangeResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_SPECIFIC_STATUS_CHANGE_ID + activeTab]
  );

  const fetchBankData = useCallback((bank: SelectableBank) => {
    if (bankDataCacheRef.current[bank].fetched || fetchingRef.current.has(bank)) {
      return;
    }

    fetchingRef.current.add(bank);
    waitingForPendingRef.current.add(bank);
    BankSelectionService.bankSpecificGet(bank, bankCreditRequestId);
    BankSelectionService.bankNotesAll(bankCreditRequestId, "BANK");
    BankSelectionService.bankNotesAll(bankCreditRequestId, "PROCESSING");
  }, [bankCreditRequestId]);

  const fetchAvailableStatuses = useCallback((bank: SelectableBank, force = false) => {
    if (force) {
      attemptedAvailableStatusesRef.current.delete(bank);
    }

    if (
      !force &&
      (
        fetchingAvailableStatusesRef.current.has(bank) ||
        attemptedAvailableStatusesRef.current.has(bank) ||
        availableStatusesRef.current[bank] !== undefined
      )
    ) {
      return;
    }

    attemptedAvailableStatusesRef.current.add(bank);
    fetchingAvailableStatusesRef.current.add(bank);
    BankSelectionService.bankSpecificAvailableStatuses(bank, bankCreditRequestId);
  }, [bankCreditRequestId]);

  useEffect(() => {
    if (isExpanded && bankCreditRequestId && !disabledBanks.includes(activeTab as Bank)) {
      fetchBankData(activeTab);
    }
  }, [isExpanded, activeTab, bankCreditRequestId, disabledBanks, fetchBankData]);

  useEffect(() => {
    if (!isExpanded || !bankCreditRequestId || disabledBanks.includes(activeTab as Bank)) {
      return;
    }

    fetchAvailableStatuses(activeTab);
  }, [isExpanded, activeTab, bankCreditRequestId, disabledBanks, fetchAvailableStatuses]);

  useEffect(() => {
    BANK_TABS.forEach(({ bank }) => {
      const response = serviceObjects[REQUEST_BANK_SPECIFIC_AVAILABLE_STATUSES_ID + bank];

      if (Array.isArray(response?.data)) {
        setAvailableStatuses((prev) => ({ ...prev, [bank]: response.data as BankSpecificStatus[] }));
        fetchingAvailableStatusesRef.current.delete(bank);
        return;
      }

      if (response?.status === REQUEST_ERROR) {
        setAvailableStatuses((prev) => ({ ...prev, [bank]: null }));
        fetchingAvailableStatusesRef.current.delete(bank);
      }
    });
  }, [serviceObjects]);

  useEffect(() => {
    if (!fetchingRef.current.has(activeTab)) {
      return;
    }

    if (bankSpecificResponse?.status === REQUEST_PENDING) {
      waitingForPendingRef.current.delete(activeTab);
      return;
    }

    if (waitingForPendingRef.current.has(activeTab)) {
      return;
    }

    if (bankSpecificResponse?.data?.id) {
      setBankDataCache((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          bankSpecific: bankSpecificResponse.data,
          fetched: true,
        },
      }));
      if (bankSpecificResponse.data.bankStatus) {
        setBankStatuses((prev) => ({
          ...prev,
          [activeTab]: bankSpecificResponse.data.bankStatus,
        }));
      }
    } else {
      setBankDataCache((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          fetched: true,
        },
      }));
      setBankStatuses((prev) => ({
        ...prev,
        [activeTab]: "IN_PREPARATION",
      }));
    }

    fetchingRef.current.delete(activeTab);
  }, [bankSpecificResponse, activeTab]);

  useEffect(() => {
    if (bankNotesResponse?.status !== "ERROR" && bankNotesResponse?.data) {
      setBankDataCache((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          bankNotes: Array.isArray(bankNotesResponse.data) ? bankNotesResponse.data : [],
        },
      }));
    }
  }, [bankNotesResponse, activeTab]);

  useEffect(() => {
    if (processingNotesResponse?.status !== "ERROR" && processingNotesResponse?.data) {
      setBankDataCache((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          processingNotes: Array.isArray(processingNotesResponse.data) ? processingNotesResponse.data : [],
        },
      }));
    }
  }, [processingNotesResponse, activeTab]);

  useEffect(() => {
    if (noteCreateResponse?.status !== "ERROR" && noteCreateResponse?.data?.id) {
      const noteType = noteCreateResponse.data.noteType as NoteType | undefined;
      const notesKey = noteType === "PROCESSING" ? "processingNotes" : "bankNotes";

      setBankDataCache((prev) => {
        const currentNotes = prev[activeTab][notesKey];
        const exists = currentNotes.some((n) => n.id === noteCreateResponse.data.id);
        if (!exists) {
          return {
            ...prev,
            [activeTab]: {
              ...prev[activeTab],
              [notesKey]: [...currentNotes, noteCreateResponse.data],
            },
          };
        }
        return prev;
      });
    }
  }, [noteCreateResponse, activeTab]);

  useEffect(() => {
    if (bankSpecificSaveResponse?.status !== "ERROR" && bankSpecificSaveResponse?.data?.id) {
      const savedBank = bankSpecificSaveResponse.data.bank as SelectableBank;
      setBankDataCache((prev) => ({
        ...prev,
        [savedBank]: {
          ...prev[savedBank],
          bankSpecific: bankSpecificSaveResponse.data,
        },
      }));
      if (bankSpecificSaveResponse.data.bankStatus) {
        setBankStatuses((prev) => ({
          ...prev,
          [savedBank]: bankSpecificSaveResponse.data.bankStatus,
        }));
      }
      fetchAvailableStatuses(savedBank, true);
      onBankStatusUpdated?.();
    }
  }, [bankSpecificSaveResponse, fetchAvailableStatuses, onBankStatusUpdated]);

  useEffect(() => {
    if (bankSpecificStatusChangeResponse?.status !== "ERROR" && bankSpecificStatusChangeResponse?.data?.id) {
      const updatedBank = bankSpecificStatusChangeResponse.data.bank as SelectableBank;
      setBankDataCache((prev) => ({
        ...prev,
        [updatedBank]: {
          ...prev[updatedBank],
          bankSpecific: bankSpecificStatusChangeResponse.data,
        },
      }));
      if (bankSpecificStatusChangeResponse.data.bankStatus) {
        setBankStatuses((prev) => ({
          ...prev,
          [updatedBank]: bankSpecificStatusChangeResponse.data.bankStatus,
        }));
      }
      fetchAvailableStatuses(updatedBank, true);
      onBankStatusUpdated?.();
    }
  }, [bankSpecificStatusChangeResponse, fetchAvailableStatuses, onBankStatusUpdated]);

  useEffect(() => {
    const calculateDistance = async () => {
      if (!clientAddress || distanceCalculating || distanceFromOffice !== undefined) {
        return;
      }

      const clientAddressString = DistanceUtils.buildAddressString(
        clientAddress.street,
        clientAddress.houseNumber,
        clientAddress.postal,
        clientAddress.city
      );

      if (!clientAddressString || clientAddressString.length < 5) {
        return;
      }

      if (attemptedDistanceAddressRef.current === clientAddressString) {
        return;
      }

      attemptedDistanceAddressRef.current = clientAddressString;
      setDistanceCalculating(true);
      try {
        const result = await DistanceUtils.calculateCommuteDistance(clientAddressString, OFFICE_ADDRESS);
        setDistanceFromOffice(Math.round(result.estimatedRoadKm * 10) / 10);
      } catch (error) {
        console.error("[Distance] Error calculating distance to office:", error);
        setDistanceFromOffice(undefined);
      } finally {
        setDistanceCalculating(false);
      }
    };

    calculateDistance();
  }, [clientAddress, distanceCalculating, distanceFromOffice]);

  const handleAddNote = useCallback((note: string, noteType: NoteType) => {
    BankSelectionService.bankNoteCreate({
      bankCreditRequestId,
      note,
      noteType,
    });
  }, [bankCreditRequestId]);

  const handleStatusChange = (bank: SelectableBank, status: BankSpecificStatus) => {
    const bankSpecific = bankDataCache[bank].bankSpecific;

    if (bankSpecific?.id) {
      BankSelectionService.bankSpecificChangeStatus(bankSpecific.id, status, bank);
    } else {
      BankSelectionService.bankSpecificSave({
        bank,
        bankCreditRequestId,
        bankStatus: status,
      });
    }
  };

  const handleSaveRequestInfo = (requestNumber: string, collaboratorName: string) => {
    const bankSpecific = bankDataCache[activeTab].bankSpecific;
    const currentStatus = bankStatuses[activeTab];

    BankSelectionService.bankSpecificSave({
      bank: activeTab,
      bankCreditRequestId,
      bankStatus: currentStatus,
      requestNumber,
      collaboratorName,
      maxCreditAmount: bankSpecific?.maxCreditAmount,
      maxPaymentPeriod: bankSpecific?.maxPaymentPeriod,
    });
  };

  const handleDocLanguageChange = (language: DocLanguage) => {
    const bankSpecific = bankDataCache[activeTab].bankSpecific;
    const currentStatus = bankStatuses[activeTab];

    BankSelectionService.bankSpecificSave({
      bank: activeTab,
      bankCreditRequestId,
      bankStatus: currentStatus,
      requestNumber: bankSpecific?.requestNumber,
      collaboratorName: bankSpecific?.collaboratorName,
      docLanguage: language,
      maxCreditAmount: bankSpecific?.maxCreditAmount,
      maxPaymentPeriod: bankSpecific?.maxPaymentPeriod,
    });
  };

  const handleMaxCreditChange = (value: number | undefined) => {
    const bankSpecific = bankDataCache[activeTab].bankSpecific;
    const currentStatus = bankStatuses[activeTab];

    BankSelectionService.bankSpecificSave({
      bank: activeTab,
      bankCreditRequestId,
      bankStatus: currentStatus,
      requestNumber: bankSpecific?.requestNumber,
      collaboratorName: bankSpecific?.collaboratorName,
      docLanguage: bankSpecific?.docLanguage,
      maxCreditAmount: value,
      maxPaymentPeriod: bankSpecific?.maxPaymentPeriod,
    });
  };

  const handleMaxPeriodChange = (value: number | undefined) => {
    const bankSpecific = bankDataCache[activeTab].bankSpecific;
    const currentStatus = bankStatuses[activeTab];

    BankSelectionService.bankSpecificSave({
      bank: activeTab,
      bankCreditRequestId,
      bankStatus: currentStatus,
      requestNumber: bankSpecific?.requestNumber,
      collaboratorName: bankSpecific?.collaboratorName,
      docLanguage: bankSpecific?.docLanguage,
      maxCreditAmount: bankSpecific?.maxCreditAmount,
      maxPaymentPeriod: value,
    });
  };

  const handleTabClick = (bank: SelectableBank) => {
    if (!disabledBanks.includes(bank as Bank)) {
      setActiveTab(bank);
    }
  };

  return (
    <Box
      p="md"
      style={{
        border: "1px solid rgba(50, 51, 67, 0.15)",
        borderRadius: 12,
      }}
    >
      <Flex gap="md" mb="md" justify="space-between" align="center">
        <Flex gap="md">
          {BANK_TABS.map(({ bank, labelKey, logo }) => {
            const isDisabled = disabledBanks.includes(bank as Bank);
            const isActive = activeTab === bank;

            return (
              <Flex
                key={bank}
                align="center"
                gap="sm"
                px="md"
                py="xs"
                onClick={() => handleTabClick(bank)}
                style={{
                  border: "1px solid rgba(50, 51, 67, 0.15)",
                  borderRadius: 8,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.5 : 1,
                  backgroundColor: isActive ? "rgba(50, 51, 67, 0.03)" : "transparent",
                }}
              >
                <Image src={logo} alt={t(labelKey)} w={28} h={28} fit="contain" mr={-4} />
                <Text fw={500}>{t(labelKey)}</Text>
              </Flex>
            );
          })}
        </Flex>
        {canGenerateXml && (
          <Button
            variant="outline"
            color="gray"
            size="compact-sm"
            loading={isXmlExporting}
            onClick={handleXmlExport}
          >
            {isXmlExporting
              ? t("bankSelection.details.generatingXml")
              : t("bankSelection.details.generateXml")}
          </Button>
        )}
      </Flex>

      {isExpanded && (
        <Box>
          {!disabledBanks.includes(activeTab as Bank) && (
            <Flex mb="md" justify="flex-start" align="center" gap="sm">
              <Text size="sm" fw={500}>
                {t("bankSelection.status.bankLabel")}
              </Text>
              <StatusBadge
                status={bankStatuses[activeTab]}
                statuses={availableStatuses[activeTab] ?? undefined}
                onChange={(status) => handleStatusChange(activeTab, status)}
                loading={
                  fetchingAvailableStatusesRef.current.has(activeTab) ||
                  serviceObjects[REQUEST_BANK_SPECIFIC_STATUS_CHANGE_ID + activeTab]?.status === REQUEST_PENDING ||
                  serviceObjects[REQUEST_BANK_SPECIFIC_SAVE_ID]?.status === REQUEST_PENDING
                }
              />
            </Flex>
          )}

          <BankTab
            key={activeTab}
            bank={activeTab}
            clientId={clientId}
            creditId={creditId}
            bankCreditRequestId={bankCreditRequestId}
            availableBanks={availableBanks}
            bankSpecific={bankDataCache[activeTab].bankSpecific}
            bankStatus={bankStatuses[activeTab]}
            bankNotes={bankDataCache[activeTab].bankNotes}
            processingNotes={bankDataCache[activeTab].processingNotes}
            distanceFromOffice={distanceFromOffice}
            onlineAuthEligible={isOnlineAuthEligible}
            clientVisaType={clientVisaType}
            onAddNote={handleAddNote}
            onSaveRequestInfo={handleSaveRequestInfo}
            onDocLanguageChange={handleDocLanguageChange}
            onMaxCreditChange={handleMaxCreditChange}
            onMaxPeriodChange={handleMaxPeriodChange}
          />
        </Box>
      )}
    </Box>
  );
};
